import {
  ArrowClockwise,
  CheckCircle,
  LinkSimple,
  ShieldCheck,
  XCircle,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import api from "../lib/api";


const ENDPOINT = "/admin/affiliate-campaigns/botb/confido";
const ROUTE_LABELS = {
  main: "Dream Car / main catalogue",
  house: "Dream Home / property",
  instantwin: "Instant Wins",
};
const VERIFIABLE_TEST_STATUSES = new Set([
  "approved_destination_observed",
  "interstitial_present",
  "tracking_endpoint_responded",
]);

const errorText = (error) => (
  error?.response?.data?.detail?.error
  || error?.response?.data?.detail
  || error?.message
  || "Unknown request error"
);

const replaceCampaign = (campaigns, replacement, { select = false } = {}) => (
  campaigns.map((campaign) => {
    if (
      select
      && campaign.route_family === replacement.route_family
      && campaign.campaign_key !== replacement.campaign_key
    ) {
      return { ...campaign, selected: false, active_route_family: null };
    }
    return campaign.campaign_key === replacement.campaign_key
      ? replacement
      : campaign;
  })
);

export default function BotbConfidoCampaignsCard() {
  const [campaigns, setCampaigns] = useState([]);
  const [approvedDomains, setApprovedDomains] = useState([]);
  const [finalDestinations, setFinalDestinations] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const loadCampaigns = async () => {
    setBusy((current) => current || "load");
    setError("");
    try {
      const { data } = await api.get(ENDPOINT);
      setCampaigns(data.campaigns || []);
      setApprovedDomains(data.approved_destination_domains || []);
      setFinalDestinations((current) => {
        const next = { ...current };
        for (const campaign of data.campaigns || []) {
          if (!next[campaign.campaign_key]) {
            next[campaign.campaign_key] = (
              campaign.final_destination_url
              || campaign.observed_final_destination
              || ""
            );
          }
        }
        return next;
      });
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy((current) => (current === "load" ? null : current));
    }
  };

  useEffect(() => {
    loadCampaigns();
    // This card is mounted only for BOTB; one initial admin fetch is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testCampaign = async (campaign) => {
    setBusy(`test:${campaign.campaign_key}`);
    setError("");
    try {
      const { data } = await api.post(
        `${ENDPOINT}/${campaign.campaign_key}/test`,
        {},
      );
      setCampaigns((current) => replaceCampaign(current, data.campaign));
      setFinalDestinations((current) => ({
        ...current,
        [campaign.campaign_key]: (
          data.campaign.final_destination_url
          || data.campaign.observed_final_destination
          || current[campaign.campaign_key]
          || ""
        ),
      }));
      toast.success(`Confido ${campaign.campaign_key} test completed.`);
    } catch (requestError) {
      const message = errorText(requestError);
      setError(message);
      toast.error(`Confido test failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const verifyCampaign = async (campaign) => {
    const finalDestinationUrl = String(
      finalDestinations[campaign.campaign_key] || "",
    ).trim();
    if (!finalDestinationUrl) return;
    if (!window.confirm(
      `Mark ${campaign.campaign_key} verified for ${finalDestinationUrl}? `
      + "This does not enable routing.",
    )) return;
    setBusy(`verify:${campaign.campaign_key}`);
    setError("");
    try {
      const { data } = await api.post(
        `${ENDPOINT}/${campaign.campaign_key}/verify`,
        { final_destination_url: finalDestinationUrl, confirm: true },
      );
      setCampaigns((current) => replaceCampaign(current, data.campaign));
      toast.success(`${campaign.campaign_key} is verified but remains disabled.`);
    } catch (requestError) {
      const message = errorText(requestError);
      setError(message);
      toast.error(`Verification failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const selectCampaign = async (campaign) => {
    const label = ROUTE_LABELS[campaign.route_family] || campaign.route_family;
    if (!window.confirm(
      `Select ${campaign.campaign_key} for ${label}? This enables that exact `
      + "verified campaign and replaces the active variant for this route family.",
    )) return;
    setBusy(`select:${campaign.campaign_key}`);
    setError("");
    try {
      const { data } = await api.post(
        `${ENDPOINT}/${campaign.campaign_key}/select`,
        { route_family: campaign.route_family, confirm: true },
      );
      setCampaigns((current) => replaceCampaign(
        current,
        data.campaign,
        { select: true },
      ));
      toast.success(`${campaign.campaign_key} now routes ${label}.`);
    } catch (requestError) {
      const message = errorText(requestError);
      setError(message);
      toast.error(`Campaign selection failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const disableCampaign = async (campaign) => {
    setBusy(`disable:${campaign.campaign_key}`);
    setError("");
    try {
      const { data } = await api.post(
        `${ENDPOINT}/${campaign.campaign_key}/disable`,
        {},
      );
      setCampaigns((current) => replaceCampaign(current, data.campaign));
      toast.success(`${campaign.campaign_key} disabled; clean-source fallback restored.`);
    } catch (requestError) {
      const message = errorText(requestError);
      setError(message);
      toast.error(`Disable failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const restoreFallback = async (routeFamily) => {
    const label = ROUTE_LABELS[routeFamily] || routeFamily;
    if (!window.confirm(`Restore clean BOTB source URLs for ${label}?`)) return;
    setBusy(`fallback:${routeFamily}`);
    setError("");
    try {
      const { data } = await api.post(
        `${ENDPOINT}/route-families/${routeFamily}/fallback`,
        { confirm: true },
      );
      setCampaigns(data.campaigns || []);
      toast.success(`Clean-source fallback restored for ${label}.`);
    } catch (requestError) {
      const message = errorText(requestError);
      setError(message);
      toast.error(`Fallback failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="w-full rounded border border-amber-400/25 bg-amber-400/[0.055] p-3 space-y-3"
      data-testid="botb-confido-campaigns-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-100">
            <LinkSimple size={15} weight="duotone" />
            BOTB Confido campaigns
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-white/60 max-w-3xl">
            Only a verified, enabled campaign selected for its compatible route
            family can replace the clean BOTB source URL. Base and 2 variants
            remain independent until explicitly tested and selected.
          </p>
        </div>
        <button
          type="button"
          onClick={loadCampaigns}
          disabled={busy != null}
          className="px-3 py-1.5 rounded text-[11px] inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white disabled:opacity-45"
          data-testid="botb-confido-refresh"
        >
          <ArrowClockwise size={12} /> Refresh
        </button>
      </div>

      <div
        className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
        data-testid="botb-confido-interstitial-warning"
      >
        Confido currently presents the first-visit tracking activation countdown.
        It is a Confido page, not a Competition Checker redirect or timer.
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.keys(ROUTE_LABELS).map((routeFamily) => (
          <button
            key={routeFamily}
            type="button"
            onClick={() => restoreFallback(routeFamily)}
            disabled={busy != null}
            className="px-3 py-1.5 rounded text-[11px] inline-flex items-center gap-1.5 border border-white/10 bg-black/15 text-white/75 hover:text-white disabled:opacity-45"
            data-testid={`botb-confido-fallback-${routeFamily}`}
          >
            <XCircle size={12} /> Clean-source fallback: {ROUTE_LABELS[routeFamily]}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-[11px] text-red-200" data-testid="botb-confido-error">
          {error}
        </div>
      )}
      {busy === "load" && campaigns.length === 0 && (
        <div className="text-[11px] text-white/50">Loading campaign status...</div>
      )}

      <div className="grid gap-2 lg:grid-cols-2">
        {campaigns.map((campaign) => {
          const verified = campaign.verification_status === "verified";
          const selected = campaign.selected === true;
          const finalDestination = finalDestinations[campaign.campaign_key] || "";
          return (
            <article
              key={campaign.campaign_key}
              className="rounded border border-white/10 bg-black/15 p-3 space-y-2"
              data-testid={`botb-confido-campaign-${campaign.campaign_key}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold text-white">
                    {campaign.campaign_key}
                  </div>
                  <div className="text-[10px] text-white/45">
                    {campaign.campaign_variant === "variant_2" ? "Alternate 2 variant" : "Base variant"}
                    {" / "}{ROUTE_LABELS[campaign.route_family]}
                  </div>
                </div>
                <span className={`text-[10px] uppercase ${selected ? "text-emerald-200" : "text-white/45"}`}>
                  {selected ? "Active" : campaign.campaign_status}
                </span>
              </div>

              <a
                href={campaign.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-[10px] text-amber-200 break-all hover:underline"
              >
                {campaign.tracking_url}
              </a>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-white/55">
                <span>Verification: {campaign.verification_status}</span>
                <span>Enabled: {String(campaign.enabled === true)}</span>
                <span>Last tested: {campaign.last_tested_at || "never"}</span>
                <span>Health: {campaign.last_test_status || "not tested"}</span>
                <span>Interstitial: {campaign.interstitial_present ? "Confido countdown" : "not detected"}</span>
                <span>Payout: {campaign.payout_currency} {campaign.payout_value} {campaign.payout_type}</span>
              </div>
              <div className="text-[10px] text-white/50 break-all">
                Observed destination: {campaign.observed_final_destination || "unverified"}
              </div>
              {campaign.health_result?.error && (
                <div className="text-[10px] text-red-200 break-words">
                  {campaign.health_result.error}
                </div>
              )}

              <label className="block text-[10px] text-white/55">
                Verified final BOTB URL
                <input
                  type="url"
                  value={finalDestination}
                  onChange={(event) => setFinalDestinations((current) => ({
                    ...current,
                    [campaign.campaign_key]: event.target.value,
                  }))}
                  placeholder="https://www.botb.com/..."
                  className="mt-1 w-full rounded border border-white/10 bg-black/20 px-2 py-1.5 text-xs text-white"
                  data-testid={`botb-confido-final-url-${campaign.campaign_key}`}
                />
              </label>

              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => testCampaign(campaign)}
                  disabled={busy != null}
                  className="px-2.5 py-1.5 rounded text-[10px] inline-flex items-center gap-1 bg-white/10 text-white disabled:opacity-45"
                  data-testid={`botb-confido-test-${campaign.campaign_key}`}
                >
                  <ArrowClockwise size={11} /> Test campaign redirect
                </button>
                <button
                  type="button"
                  onClick={() => verifyCampaign(campaign)}
                  disabled={
                    busy != null
                    || !finalDestination.trim()
                    || !VERIFIABLE_TEST_STATUSES.has(campaign.last_test_status)
                  }
                  className="px-2.5 py-1.5 rounded text-[10px] inline-flex items-center gap-1 bg-sky-500/15 text-sky-100 disabled:opacity-45"
                  data-testid={`botb-confido-verify-${campaign.campaign_key}`}
                >
                  <ShieldCheck size={11} /> Mark destination verified
                </button>
                <button
                  type="button"
                  onClick={() => selectCampaign(campaign)}
                  disabled={busy != null || !verified}
                  className="px-2.5 py-1.5 rounded text-[10px] inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-100 disabled:opacity-45"
                  data-testid={`botb-confido-select-${campaign.campaign_key}`}
                >
                  <CheckCircle size={11} /> Select for route family
                </button>
                <button
                  type="button"
                  onClick={() => disableCampaign(campaign)}
                  disabled={busy != null}
                  className="px-2.5 py-1.5 rounded text-[10px] inline-flex items-center gap-1 bg-red-500/15 text-red-100 disabled:opacity-45"
                  data-testid={`botb-confido-disable-${campaign.campaign_key}`}
                >
                  <XCircle size={11} /> Disable campaign
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <div className="text-[10px] text-white/45">
        Approved final domains: {approvedDomains.join(", ") || "loading"}.
        Clean BOTB source URLs remain stored and are always the fallback.
      </div>
    </section>
  );
}
